import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";

/**
 * MSAL wiring for Entra External ID (S1.1/S1.2).
 *
 * A single module-level instance so `apiFetch` can attach a bearer token to
 * every request without each call site knowing about auth. Configured at runtime
 * from `GET /api/auth/config` (no rebuild to change tenant), so nothing here is
 * hardcoded — including the authority, whose host we register as a known
 * authority because a `*.ciamlogin.com` CIAM authority is not a built-in one.
 */

export type MsalRuntimeConfig = {
  authority: string;
  clientId: string;
  scopes: string[];
};

let pca: PublicClientApplication | null = null;
let apiScopes: string[] = [];
let redirectState: string | null = null;

/**
 * Initialize MSAL and process any redirect we are returning from. Call once,
 * before rendering. Captures the `state` from a redirect (used to carry an
 * invite token through sign-in, S2.2) for the caller to consume.
 */
export async function initMsal(config: MsalRuntimeConfig): Promise<void> {
  pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      // A CIAM (`*.ciamlogin.com`) authority is not a built-in Microsoft one, so
      // MSAL needs it explicitly allow-listed.
      knownAuthorities: [new URL(config.authority).host],
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    },
    // localStorage so a token survives a tab reload; the API re-checks the
    // active flag on every request, so a cached token is never a stale grant.
    cache: { cacheLocation: "localStorage" },
  });
  apiScopes = config.scopes;

  await pca.initialize();

  const result = await pca.handleRedirectPromise();
  if (result?.account) {
    pca.setActiveAccount(result.account);
    redirectState = result.state ?? null;
  } else if (!pca.getActiveAccount()) {
    const [first] = pca.getAllAccounts();
    if (first) pca.setActiveAccount(first);
  }
}

export function isConfigured(): boolean {
  return pca !== null;
}

// ── Dev sign-in (dev builds only) ────────────────────────────────────────────
// A dev token stands in for a real Entra access token when the app runs without
// interactive sign-in (local dev, E2E). The whole branch is behind
// `import.meta.env.DEV`, so a production `vite build` tree-shakes it away — there
// is no code path to a dev token in a production bundle.
const DEV_TOKEN_KEY = "dev-access-token";

export function setDevToken(token: string): void {
  if (import.meta.env.DEV) sessionStorage.setItem(DEV_TOKEN_KEY, token);
}

export function clearDevToken(): void {
  if (import.meta.env.DEV) sessionStorage.removeItem(DEV_TOKEN_KEY);
}

function devToken(): string | null {
  return import.meta.env.DEV ? sessionStorage.getItem(DEV_TOKEN_KEY) : null;
}

export function hasDevToken(): boolean {
  return devToken() !== null;
}

export function currentAccount(): AccountInfo | null {
  return pca?.getActiveAccount() ?? null;
}

/** The `state` from the redirect we just returned from, consumed once. */
export function consumeRedirectState(): string | null {
  const s = redirectState;
  redirectState = null;
  if (s) activatedAtBootstrap = s;
  return s;
}

// The invite token bootstrap took from the redirect and activated (or failed to).
// MSAL's `navigateToLoginRequestUrl` (on by default) returns the browser to the
// page that started sign-in — `/activate?token=…` — so that page mounts right
// after bootstrap has already activated its token. Without this it activated
// again, was refused ("no longer valid", the invite is ACCEPTED now), and told
// a freshly activated contractor their link had failed.
let activatedAtBootstrap: string | null = null;

/** Whether bootstrap already handled this invite token on this page load. */
export function handledAtBootstrap(token: string): boolean {
  return activatedAtBootstrap === token;
}

/**
 * A bearer access token for the API, or null when nobody is signed in. Silent
 * refresh handled by MSAL; if the token needs interaction it triggers a redirect
 * and returns null (the page reloads after sign-in).
 */
export async function getAccessToken(): Promise<string | null> {
  // A dev token (dev builds only) short-circuits MSAL entirely.
  const dev = devToken();
  if (dev) return dev;

  if (!pca) return null;
  const account = pca.getActiveAccount();
  if (!account) return null;
  try {
    const result = await pca.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await pca.acquireTokenRedirect({ scopes: apiScopes, account });
    }
    return null;
  }
}

/** Start sign-in. `state` rides the round-trip — used to carry an invite token. */
export async function login(state?: string): Promise<void> {
  if (!pca) throw new Error("Sign-in is not configured.");
  await pca.loginRedirect({ scopes: apiScopes, ...(state ? { state } : {}) });
}

/** Clear MSAL's cache and redirect to Entra logout, then back to /login. */
export async function logout(): Promise<void> {
  clearDevToken();
  if (!pca) {
    // Dev sign-in has no Entra session to end; a hard reload to /login is enough.
    if (import.meta.env.DEV) window.location.assign("/login");
    return;
  }
  await pca.logoutRedirect();
}
